import { useMutation } from '@tanstack/react-query'
import { notification } from '@surecompanies/core_components'
import { AxiosError } from 'axios'
import { uploadPaymentBulkCsv } from './payment-bulk-api'
import { postActivityLog } from 'modules/activity-log-service/helpers'
import { ActivityLogAction } from 'modules/activity-log-service/constants'

export default function usePaymentBulkUpload() {
  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadPaymentBulkCsv(file),
    onSuccess: async (_, file) => {
      notification.success({
        message: 'CSV file uploaded successfully',
      })

      // Log successful upload activity
      postActivityLog({
        action: ActivityLogAction.PAYMENT_BULK_UPLOAD,
        description: `uploaded payment bulk transaction file: ${file.name}`,
        additionalData: {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadTimestamp: new Date().toISOString(),
        },
      })
    },
    onError: async (error: unknown, file) => {
      let errorMessage = 'An unexpected error occurred'

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError
        errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          'Something went wrong while uploading the file'

        notification.error({
          message: 'Failed to upload CSV file',
          description: errorMessage,
        })
      } else {
        notification.error({
          message: 'Failed to upload CSV file',
          description: errorMessage,
        })
      }

      // Log failed upload activity
      postActivityLog({
        action: ActivityLogAction.PAYMENT_BULK_UPLOAD,
        description: `failed to upload payment bulk transaction file: ${file.name}`,
        additionalData: {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          errorMessage,
          uploadTimestamp: new Date().toISOString(),
        },
      })
    },
  })

  const handleUpload = (file: File) => {
    uploadMutation.mutate(file)
  }

  return {
    handleUpload,
    isUploading: uploadMutation.isPending,
    uploadResult: uploadMutation.data,
    error: uploadMutation.error,
  }
}
